import { Checkbox, Grid, TextField, MenuItem, FormHelperText, FormLabel, ListItemText } from '@material-ui/core';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import {
    ResponsiveSidebarDialog,
    ModalTitle,
    ModalContent,
    StickyModalActions,
    SecondaryButton,
    PrimaryButton,
} from '@/styles/blocks';
import { ASSIGN_ZIP_TYPES, ORDER_TYPE_OPTIONS, ORDER_TYPE_LABELS } from './constants';
import { COMMODITY_OPTIONS, PROFILE_DELIVERY_TYPES } from './profileConstants';
import { useDeliveryProfileForm } from './hooks';
import ToggleBadge from './ToggleBadge';

// Menu items for a multi-select, each with a checkbox so it reads as pick-many.
const checkboxOptions = (options, selected) =>
    options.map(({ value, label }) => (
        <MenuItem key={value} value={value}>
            <Checkbox
                size="small"
                checked={selected.includes(value)}
                style={{ color: selected.includes(value) ? colors.greens.primary : undefined }}
            />
            <ListItemText primary={label} />
        </MenuItem>
    ));

const DeliveryProfileModal = ({ open, onClose, deliveryProfile, callbacks }) => {
    const {
        isEdit,
        form,
        saving,
        errorFor,
        availableRequirements,
        mixedZipTypes,
        setField,
        onCommodityChange,
        onOrderTypesChange,
        toggleInArray,
        save,
    } = useDeliveryProfileForm({
        open,
        deliveryProfile,
        upsertDeliveryProfile: callbacks.upsertDeliveryProfile,
        onClose,
    });

    return (
        <ResponsiveSidebarDialog open={open} onClose={onClose} maxWidth="md">
            <ModalTitle title={isEdit ? 'Edit Delivery Profile' : 'Create Delivery Profile'} onClose={onClose} />
            <ModalContent width={860}>
                <Grid
                    container
                    direction="column"
                    css={css`
                        gap: 20px;
                    `}
                >
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Profile Name"
                        id="delivery-profile-name"
                        value={form.name}
                        InputLabelProps={{ htmlFor: 'delivery-profile-name', shrink: !!form.name }}
                        error={!!errorFor('name')}
                        helperText={errorFor('name')}
                        onChange={(e) => setField('name', e.target.value)}
                    />

                    <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Assign by zip"
                        id="delivery-profile-assign-zip-type"
                        InputLabelProps={{ htmlFor: 'delivery-profile-assign-zip-type' }}
                        value={form.assign_zip_type}
                        error={!!errorFor('assign_zip_type')}
                        helperText={
                            errorFor('assign_zip_type') ||
                            (mixedZipTypes
                                ? 'Shippers on this profile use different zip types. Changing this sets all of them.'
                                : undefined)
                        }
                        onChange={(e) => setField('assign_zip_type', e.target.value)}
                    >
                        {ASSIGN_ZIP_TYPES.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Profile-level default the apply bar seeds from. Editing it never rewrites
                        existing assignments. */}
                    <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Order Types"
                        id="delivery-profile-order-types"
                        InputLabelProps={{ htmlFor: 'delivery-profile-order-types' }}
                        value={form.default_order_types}
                        error={!!errorFor('default_order_types')}
                        helperText={errorFor('default_order_types')}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => selected.map((v) => ORDER_TYPE_LABELS[v] || v).join(', '),
                            MenuProps: { variant: 'menu', getContentAnchorEl: null },
                        }}
                        onChange={(e) => onOrderTypesChange(e.target.value)}
                    >
                        {checkboxOptions(ORDER_TYPE_OPTIONS, form.default_order_types)}
                    </TextField>

                    <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Commodity"
                        id="delivery-profile-commodity"
                        InputLabelProps={{ htmlFor: 'delivery-profile-commodity' }}
                        value={form.commodity}
                        error={!!errorFor('commodity')}
                        helperText={errorFor('commodity')}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) =>
                                selected
                                    .map((v) => COMMODITY_OPTIONS.find((o) => o.value === v)?.label || v)
                                    .join(', '),
                            MenuProps: { variant: 'menu', getContentAnchorEl: null },
                        }}
                        onChange={(e) => onCommodityChange(e.target.value)}
                    >
                        {checkboxOptions(COMMODITY_OPTIONS, form.commodity)}
                    </TextField>

                    <Grid item>
                        {/* FormLabel has no field to infer `required` from, unlike TextField. */}
                        <FormLabel
                            required
                            css={css`
                                font-size: 14px;
                            `}
                        >
                            Service Types:
                        </FormLabel>
                        {/* Wrapper must stay bare: scoped utilities only match DESCENDANTS, so
                            layout classes go on the child. */}
                        <div className="onward-design">
                            <div className="mt-2 flex flex-wrap gap-2">
                                {PROFILE_DELIVERY_TYPES.map(({ value, label }) => (
                                    <ToggleBadge
                                        key={value}
                                        label={label}
                                        selected={form.delivery_types.includes(value)}
                                        onToggle={() => toggleInArray('delivery_types', value)}
                                    />
                                ))}
                            </div>
                        </div>
                        {errorFor('delivery_types') ? (
                            <FormHelperText error>{errorFor('delivery_types')}</FormHelperText>
                        ) : null}
                    </Grid>

                    <Grid item>
                        <FormLabel
                            css={css`
                                font-size: 14px;
                            `}
                        >
                            Special requirements:
                        </FormLabel>
                        {form.commodity.length ? (
                            availableRequirements.length ? (
                                <div className="onward-design">
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {availableRequirements.map(({ value, label }) => (
                                            <ToggleBadge
                                                key={value}
                                                label={label}
                                                selected={form.special_requirements.includes(value)}
                                                onToggle={() => toggleInArray('special_requirements', value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <FormHelperText>No special requirements apply to these commodities.</FormHelperText>
                            )
                        ) : (
                            <FormHelperText>Select a commodity to see available requirements.</FormHelperText>
                        )}
                    </Grid>
                </Grid>
            </ModalContent>
            <StickyModalActions>
                <Grid item>
                    <SecondaryButton onClick={onClose} disabled={saving}>
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid item>
                    <PrimaryButton onClick={save} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default DeliveryProfileModal;
